import { Account } from '@/lib/redux/app.slice';
import { SearchBar } from '@/shared/components/SearchBar/SearchBar';
import React from 'react';
import { createPortal } from 'react-dom';
import AccountParentChain from './AccountParentChain';
import AccountSelectorDropdownItem from './AccountSelectorDropdownItem';

interface AccountSelectorDropdownProps {
  isOpen: boolean;
  position: { top: number; left: number };
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filteredAccounts: Account[];
  currentAccountId: string | null;
  onAccountSelect: (account: Account) => void;
  parentChain: Account[];
  canSwitchTo: (accountId: string) => boolean;
}

const AccountSelectorDropdown: React.FC<AccountSelectorDropdownProps> = ({
  isOpen,
  position,
  dropdownRef,
  searchTerm,
  setSearchTerm,
  filteredAccounts,
  currentAccountId,
  onAccountSelect,
  parentChain,
  canSwitchTo,
}) => {
  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      ref={dropdownRef}
      tabIndex={0}
      className="bg-base-300 rounded-box fixed z-60 w-80 border border-gray-200 p-2 shadow-xl dark:border-gray-800"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}>
      {/* Search Bar */}
      <div className="mb-2">
        <SearchBar
          search={searchTerm}
          setSearch={setSearchTerm}
          placeholder="Search accounts..."
          debounceMs={300}
          className="w-full"
        />
      </div>

      {/* Parent Account Chain - only show if no search active */}
      {!searchTerm && parentChain.length > 1 && (
        <AccountParentChain
          parentChain={parentChain}
          currentAccountId={currentAccountId}
          onAccountSelect={onAccountSelect}
          canSwitchTo={canSwitchTo}
        />
      )}

      {/* Account List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredAccounts.length > 0 ? (
          <ul className="space-y-1">
            {filteredAccounts.map((account) => (
              <AccountSelectorDropdownItem
                key={account.account_id}
                account={account}
                isSelected={account.account_id === currentAccountId}
                onSelect={onAccountSelect}
              />
            ))}
          </ul>
        ) : (
          <div className="flex items-center justify-center py-4">
            <span className="text-sm text-gray-500">No accounts found</span>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AccountSelectorDropdown;
